import * as XLSX from 'xlsx';

/**
 * Verilen sipariş için ürün adını döndürür.
 * Plise ürünlerde iki ürün adı birleştirilir.
 */
const getOrderProductName = (order) => {
  if (order.productType === 'plise') {
    const name1 = order.pliseProduct1Name || '';
    const name2 = order.pliseProduct2Name || '';
    return name2 ? `${name1} + ${name2}` : name1;
  }
  const base = order.productName || '';
  const variant = order.productVariant ? ` - ${order.productVariant}` : '';
  return `${base}${variant}`.trim();
};

/**
 * Ahşap jaluzi kontrolü: productType 'ahsapJaluzi' ise %20, diğerleri %10
 */
const getKdvOrani = (order) => {
  return order.productType === 'ahsapJaluzi' ? 20 : 10;
};

/**
 * Siparişin adet bilgisini döndürür.
 * Plise ürünlerde pliseItems içindeki quantity toplamı alınır.
 */
const getOrderQuantity = (order) => {
  if (order.productType === 'plise') {
    return order.pliseItems?.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0) || 1;
  }
  return parseInt(order.quantity) || 1;
};

/**
 * Siparişin indirimli veya indirimsiz toplam tutarını döndürür.
 * Bu tutar tabloda "Toplam" sütununda gösterilen değerdir.
 */
const getOrderEffectiveTotal = (order) => {
  if (order.discountedPrice && parseFloat(order.discount) > 0) {
    return parseFloat(order.discountedPrice) || 0;
  }
  return parseFloat(order.totalPrice) || 0;
};

/**
 * QNB eFinans e-Fatura uyumlu XLSX dosyası oluşturur ve indirir.
 *
 * Öncelik kuralı:
 *  1. Vade Farkı (vadeliYuzde) doluysa → her siparişe vade farkı eklenir, iskonto 0 olur.
 *  2. Yalnızca Genel İskonto doluysa → iskonto oranı yazılır, vade yok.
 *  3. İkisi de boşsa → ne iskonto ne vade uygulanır.
 *
 * @param {Object} customerInfo - Müşteri bilgileri ({ name, ... })
 * @param {Array}  orders       - Sipariş listesi
 * @param {string|number} genelIskontoYuzde - Genel iskonto yüzdesi (müşteri bazında)
 * @param {string|number} vadeliYuzde       - Vade farkı yüzdesi (müşteri bazında)
 */
export const generateInvoiceExcel = (customerInfo, orders, genelIskontoYuzde, vadeliYuzde) => {
  const vade   = parseFloat(vadeliYuzde) || 0;
  // Vade doluysa iskonto 0'dır (öncelik vadededir)
  const iskonto = vade > 0 ? 0 : (parseFloat(genelIskontoYuzde) || 0);

  // Sütun başlıkları (QNB eFinans uyumlu sıra)
  const headers = [
    'Ürün Kodu',
    'Ürün Adı',
    'Miktar',
    'Birim',
    'Birim Fiyatı',
    'İsk./Masraf Oranı(%)',
    'KDV Oranı(%)',
    'KDV Muafiyet Kodu',
    'Harcama Tipi',
    'Satır Açıklaması',
    'Açıklama',
    'Üretici Tanımlama Bilgisi',
    'Alıcı Tanımlama Bilgisi',
    'Ürün takip numarası',
    'Lot numarası',
    'Tevkifat Türü',
    'KDV Tevkifat',
    'Özel Matrah İstisna Kodu',
    'Özel Matrah Tutar',
    'Marka Adı',
    'Makine Teçhizat Sıra No',
    'Makine ID',
    'Teknolojik Ürün Tipi',
  ];

  // Her sipariş için bir satır oluştur
  const rows = orders.map((order) => {
    const kdvOrani = getKdvOrani(order);
    const kdvCarpan = 1 + kdvOrani / 100; // %10 → 1.10, %20 → 1.20

    const effectiveTotal = getOrderEffectiveTotal(order);
    const miktar = getOrderQuantity(order);

    // Vade farkı varsa her siparişin toplam tutarına vade farkı eklenir,
    // ardından adete ve KDV'ye bölünerek KDV hariç birim fiyat bulunur.
    // Vade yoksa tutar aynen kullanılır (iskonto zaten effectiveTotal'a dahildir).
    const totalWithVade = vade > 0
      ? effectiveTotal * (1 + vade / 100)
      : effectiveTotal;

    // Birim Fiyatı = (Toplam Tutar [+vade varsa] / Adet) / KDV Çarpanı
    const birimFiyati = (totalWithVade / miktar) / kdvCarpan;

    return [
      '',                                          // Ürün Kodu (boş)
      getOrderProductName(order),                  // Ürün Adı
      miktar,                                      // Miktar
      'Adet (C62)',                                // Birim
      birimFiyati,                                 // Birim Fiyatı (KDV hariç)
      iskonto > 0 ? iskonto : '',                  // İsk./Masraf Oranı(%)
      kdvOrani,                                    // KDV Oranı(%)
      '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', // Geri kalan boş sütunlar
    ];
  });

  // Çalışma sayfası verisi: başlık + satırlar
  const wsData = [headers, ...rows];

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Sütun genişliklerini ayarla (okunabilirlik için)
  ws['!cols'] = [
    { wch: 12 }, // Ürün Kodu
    { wch: 35 }, // Ürün Adı
    { wch: 8 },  // Miktar
    { wch: 12 }, // Birim
    { wch: 16 }, // Birim Fiyatı
    { wch: 22 }, // İsk./Masraf Oranı
    { wch: 12 }, // KDV Oranı
    { wch: 18 }, // KDV Muafiyet Kodu
    { wch: 14 }, // Harcama Tipi
    { wch: 18 }, // Satır Açıklaması
    { wch: 14 }, // Açıklama
    { wch: 24 }, // Üretici Tanımlama
    { wch: 22 }, // Alıcı Tanımlama
    { wch: 20 }, // Ürün takip numarası
    { wch: 14 }, // Lot numarası
    { wch: 14 }, // Tevkifat Türü
    { wch: 14 }, // KDV Tevkifat
    { wch: 24 }, // Özel Matrah İstisna Kodu
    { wch: 18 }, // Özel Matrah Tutar
    { wch: 14 }, // Marka Adı
    { wch: 22 }, // Makine Teçhizat Sıra No
    { wch: 12 }, // Makine ID
    { wch: 20 }, // Teknolojik Ürün Tipi
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Fatura');

  // Dosya adı: müşteri adı (özel karakterleri temizle)
  const safeFileName = (customerInfo?.name || 'Fatura')
    .replace(/[/\\?%*:|"<>]/g, '_')
    .trim();

  XLSX.writeFile(wb, `${safeFileName}.xlsx`);
};
